import { defineEffect } from '../../../core/effect';
import { seg, tween } from '../../../core/effect/motion';
import { fade, place } from '../fx/common';
import { RECOVER_SLASH, lifeNumberGlyphWidth, lifeNumberTexture, makeLifeNumber, tagPlace } from '../fx/lifeNumber';

/**
 * **LP の回復**（Issue #585。指示書 `../direction/recover.md`）。
 *
 * だれがいくら回復したかを、受けた人の LP のすぐ下に数字で見せる。**pay_lpcost の鏡**（減る代わりに増える）。
 * 数字の描き方は damage / pay_lpcost と共有（`../fx/lifeNumber.js`）で、符号は「+」・差しは緑。
 * 揺れ・縁の線は無い（人が 2026-09-28 に線を消した）。「RECOVER」の文字は出さない。
 *
 * ```
 * 0     LP の数字のすぐ下に「+1000」（緑の斬り傷の差しが外側から伸び、数字が少し上から降りて止まる）
 * 150   数字を見せたまま、LP の数字とゲージが増える前の値から本来の値へ増える（`hud` の `life`）
 * 650   数字がその場で薄くなって消える
 * 800   終わり（何も残らない）
 * ```
 *
 * 演出は payload（`player` 受けた人・`amount` 回復した値）とその瞬間の HUD の値だけ。もやもやした光は使わない。
 */
const D = 800;
const OPEN = 150;
const RISE_FROM = 150;
const RISE_TO = 500;
const FADE_FROM = 650;

/** 数字の大きさ（px）。pay_lpcost と同じ */
export const SIZE = 104;
/** 帯の色（緑） */
export const RECOVER_COLOR = '#3cf07a';
/** その間 HUD に見せる LP（増える前 → 150〜500ms で本来の値へ）。値が無ければ null（頼まない） */
export function shownLife(hud, player, t) {
  const p = player === 1 ? 1 : 0;
  const from = hud?.lifeFrom?.[p];
  const to = hud?.lifeTarget?.[p];
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) return null;
  if (t < RISE_FROM) return from;
  return from + (to - from) * tween(t, RISE_FROM, RISE_TO, 'ease_out');
}

export default defineEffect({
  situation: 'recover',
  role: 'effect',
  label: 'LP の回復',
  color: RECOVER_COLOR,
  opens({ payload }) {
    if (!Number.isFinite(payload?.amount) || payload.amount <= 0) return null;
    return payload;
  },
  duration: () => D,
  sounds: () => [{ at: RISE_FROM, name: 'life_up', gain: 0.6 }],
  hud: ({ payload, localMs, hud }) => {
    const life = shownLife(hud, payload.player, localMs);
    return [{
      anchor: 'life',
      player: payload.player,
      ...(life != null ? { life } : {}),
      color: RECOVER_COLOR,
      phase: localMs,
    }];
  },

  layers: [{
    id: 'number',
    label: '回復の数字',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { payload } = ctx;
      const root = new THREE.Group();
      // 数字の描き方は damage / pay_lpcost と同じ（`../fx/lifeNumber.js`）。符号は「+」、差しは緑
      const tex = lifeNumberTexture(kit, payload.amount, SIZE, '+');
      const glyphW = Math.max(SIZE, lifeNumberGlyphWidth(tex, SIZE));
      const glyphH = Math.round(SIZE * 1.25);
      const { x: cx, y: cy, out } = tagPlace(ctx, payload.player, glyphW, glyphH);
      const n = makeLifeNumber(ctx, root, tex, { order: 10, colors: RECOVER_SLASH, name: 'recover-number' });


      return {
        root,
        update: ({ localMs: t }) => {
          // その場に出る: 差しが外側から伸び、数字が少し上から降りて止まる（飛ばない・叩きつけない）
          const open = tween(t, 0, 120, 'expo_out');
          n.lay(open);
          const drop = tween(t, 0, OPEN, 'back_out');
          const alpha = t < D ? Math.min(1, seg(t, 0, 90)) * (1 - seg(t, FADE_FROM, D)) : 0;
          n.slashes.forEach((sl) => { sl.m.position.x = sl.skew + out * (sl.w * (1 - open)) / 2; });
          place(n.g, cx, cy - 16 * (1 - drop), {});
          fade(n.g, alpha);
        },
      };
    },
  }],
}, 'recover_effect.js');
